import { ActivityLog } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSearchPages } from '../../modules/ComponentsLibrary/AdvancedSearch/useSearchPages';
import { ActivityLogClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type ActivityLogFilter = EntityFilter<ActivityLog>;
export const useBatchGetActivityLogQuery = function ({
  filter = {},
  enabled,
}: {
  filter?: ActivityLogFilter;
  enabled?: boolean;
}) {
  return useQuery({
    queryFn: async () => {
      return await ActivityLogClientService.BatchGet(ActivityLog.create(filter));
    },
    queryKey: [
      queryKeys.activityLog.root,
      queryKeys.activityLog.list,
      queryKeys.activityLog.getHash(filter),
    ],
    enabled,
  });
};

/** The filter's log 25 a page from page 0, loaded a page at a time. A failure errors. */
export const useActivityLogPages = ({
  filter = {},
  enabled,
}: {
  filter?: Omit<ActivityLogFilter, 'pageNumber' | 'withoutLimit'>;
  enabled?: boolean;
}) =>
  useSearchPages<ActivityLog>({
    queryKey: [queryKeys.activityLog.root, 'pages', queryKeys.activityLog.getHash(filter)],
    fetchPage: async (pageNumber) => {
      const res = await ActivityLogClientService.BatchGet(
        ActivityLog.create({ ...filter, pageNumber }),
      );
      if (res === undefined) {
        throw new Error('Failed to load activity log');
      }
      return res;
    },
    enabled,
  });

export const useActivityLogCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: ActivityLog) => ActivityLogClientService.Create(req),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.activityLog.root] }),
  });
};

/** Single-row ActivityLog.Get (e.g. latest for an event when orderBy activity_id DESC). */
export const useGetActivityLogQuery = function ({
  filter = {},
  enabled,
}: {
  filter?: ActivityLogFilter;
  enabled?: boolean;
}) {
  return useQuery({
    queryFn: async () => {
      const res = await ActivityLogClientService.Get(ActivityLog.create(filter));
      if (res === undefined) {
        throw new Error('Failed to load activity log');
      }
      return res.id > 0 ? res : null;
    },
    queryKey: [queryKeys.activityLog.root, 'get', queryKeys.activityLog.getHash(filter)],
    enabled,
  });
};
